/* ============================================================
   ivh-fuzzy.js — búsqueda tolerante a errores de escritura
   ------------------------------------------------------------
   Si el buscador nativo de Shopify no encuentra nada (o casi
   nada), se hace una segunda pasada "difusa" contra un índice
   local de productos (/products.json), aceptando 1–2 letras
   de diferencia por palabra. Sin acentos, sin distinción de
   mayúsculas. Se engancha en:
     1) el desplegable del buscador de la cabecera
     2) la página de resultados /search
   ============================================================ */
(function () {
  'use strict';

  var CACHE_KEY = 'ivhCatalog:v2';
  var TTL = 30 * 60 * 1000; // 30 min
  var THRESHOLD = 0.72;
  var catalogPromise = null;

  /* ---------- normalización ---------- */
  function norm(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  function tokens(s) {
    var n = norm(s);
    return n ? n.split(' ') : [];
  }

  /* ---------- distancia de edición (Levenshtein) ---------- */
  function lev(a, b) {
    if (a === b) return 0;
    var m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    var prev = new Array(n + 1), cur = new Array(n + 1), i, j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur[0] = i;
      var ca = a.charCodeAt(i - 1);
      for (j = 1; j <= n; j++) {
        var cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      }
      var t = prev; prev = cur; cur = t;
    }
    return prev[n];
  }

  /* ---------- parecido palabra-consulta vs palabra-producto (0..1) ---------- */
  function tokScore(q, t) {
    if (!q || !t) return 0;
    if (q === t) return 1;
    if (q.length >= 3 && t.indexOf(q) === 0) return 0.95;   // prefijo: "creat" -> "creatina"
    if (q.length >= 5 && t.indexOf(q) !== -1) return 0.85;  // subcadena razonablemente larga
    // longitudes muy dispares -> no es un error de tecleo
    if (Math.abs(q.length - t.length) > 3) return 0;
    var d = lev(q, t);
    var maxLen = Math.max(q.length, t.length);
    var allow = maxLen <= 4 ? 1 : maxLen <= 7 ? 1 : maxLen <= 11 ? 2 : 3; // 1–2 letras
    if (d > allow) return 0;
    return Math.max(0, 1 - d / maxLen);
  }

  function scoreProduct(qtoks, entry) {
    if (!qtoks.length) return 0;
    var total = 0, matchedAll = true;
    for (var i = 0; i < qtoks.length; i++) {
      var q = qtoks[i], best = 0;
      for (var j = 0; j < entry._t.length; j++) {
        var s = tokScore(q, entry._t[j]);
        if (s > best) { best = s; if (best === 1) break; }
      }
      if (best < 0.6) matchedAll = false;
      total += best;
    }
    if (!matchedAll) return 0;               // toda palabra de la consulta debe encajar
    return total / qtoks.length;
  }

  /* ---------- índice local de productos ---------- */
  function buildEntry(p) {
    var v = (p.variants && p.variants[0]) || {};
    var img = (p.images && p.images[0] && p.images[0].src) || v.featured_image || '';
    var avail = false;
    for (var i = 0; i < (p.variants || []).length; i++) { if (p.variants[i].available) { avail = true; break; } }
    var termStr = [p.title, p.product_type, p.vendor].concat(p.tags || []).join(' ');
    var raiz = (window.ivhStrings && window.ivhStrings.rootUrl) || '';
    return {
      title: p.title,
      url: raiz + '/products/' + p.handle,
      handle: p.handle,
      price: v.price != null ? parseFloat(v.price).toFixed(2) : '',
      compare_at_price_min: v.compare_at_price != null ? parseFloat(v.compare_at_price).toFixed(2) : '',
      img: img || '',
      available: avail,
      _t: tokens(termStr)
    };
  }

  function fetchAll() {
    var out = [], page = 1;
    function next() {
      var raizFetch = (window.ivhStrings && window.ivhStrings.rootUrl) || '';
      return fetch(raizFetch + '/products.json?limit=250&page=' + page, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) {
          var arr = (d && d.products) || [];
          for (var i = 0; i < arr.length; i++) out.push(buildEntry(arr[i]));
          if (arr.length === 250 && page < 12) { page++; return next(); }
          return out;
        });
    }
    return next();
  }

  function getCatalog() {
    if (catalogPromise) return catalogPromise;
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && Date.now() - obj.t < TTL && Array.isArray(obj.d)) {
          obj.d.forEach(function (e) { e._t = e._t && e._t.length ? e._t : tokens(e.title); });
          catalogPromise = Promise.resolve(obj.d);
          return catalogPromise;
        }
      }
    } catch (e) {}
    catalogPromise = fetchAll().then(function (list) {
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: list })); } catch (e) {}
      return list;
    }).catch(function () { return []; });
    return catalogPromise;
  }

  function search(query, limit) {
    limit = limit || 6;
    var qtoks = tokens(query).filter(function (w) { return w.length >= 2; });
    if (!qtoks.length || qtoks.join('').length < 3) return Promise.resolve([]);
    return getCatalog().then(function (cat) {
      var scored = [];
      for (var i = 0; i < cat.length; i++) {
        var sc = scoreProduct(qtoks, cat[i]);
        if (sc >= THRESHOLD) scored.push([sc + (cat[i].available ? 0.03 : 0), cat[i]]);
      }
      scored.sort(function (a, b) { return b[0] - a[0]; });
      return scored.slice(0, limit).map(function (x) { return x[1]; });
    });
  }

  window.ivhFuzzy = { search: search, prime: getCatalog };

  /* ---------- utilidades de render ---------- */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function dinero(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    var strings = window.ivhStrings || {};
    var locale = strings.locale || 'es-ES';
    var currency = strings.currency || 'EUR';
    try { return n.toLocaleString(locale, { style: 'currency', currency: currency }); }
    catch (e) { return n.toFixed(2) + ' ' + currency; }
  }
  function thumb(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + (w || 90);
  }

  /* ---------- 1) desplegable de la cabecera ---------- */
  function hookDropdown() {
    var raiz = document.querySelector('[data-ivh-buscar]');
    if (!raiz) return;
    var input = raiz.querySelector('[data-ivh-buscar-input]');
    var caja = raiz.querySelector('[data-ivh-buscar-resultados]');
    if (!input || !caja) return;

    input.addEventListener('focus', function () { getCatalog(); }, { once: true });

    var obs = new MutationObserver(function () {
      if (caja.__ivhBusy) return;
      if (!caja.querySelector('.ivh-buscar-vacio')) return; // solo cuando el nativo dice "sin resultados"
      var term = input.value.trim();
      if (term.length < 2) return;
      caja.__ivhBusy = true;
      search(term, 6).then(function (res) {
        caja.__ivhBusy = false;
        if (!res.length || input.value.trim() !== term) return;
        var stringsInline = window.ivhStrings || {};
        var html = '<p class="ivh-buscar-sugerencia">' + (stringsInline.searchMaybeMeant || 'Quizás buscabas:') + '</p>';
        html += res.map(function (p) {
          return '<a class="ivh-buscar-item" href="' + esc(p.url) + '">' +
            '<span class="ivh-buscar-item-img">' + (p.img ? '<img src="' + esc(thumb(p.img, 90)) + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="ivh-buscar-item-txt">' +
              '<span class="ivh-buscar-item-titulo">' + esc(p.title) + '</span>' +
              '<span class="ivh-buscar-item-precio">' + dinero(p.price) + '</span>' +
            '</span>' +
          '</a>';
        }).join('');
        html += '<a class="ivh-buscar-todos" href="' + (stringsInline.searchUrl || '/search') + '?q=' + encodeURIComponent(term) + '&type=product">' + (stringsInline.searchSeeAll || 'Ver todos los resultados') + '</a>';
        caja.innerHTML = html;
        caja.hidden = false;
      }).catch(function () { caja.__ivhBusy = false; });
    });
    obs.observe(caja, { childList: true, subtree: true });
  }

  /* ---------- 2) página de resultados /search ---------- */
  function hookResultsPage() {
    var mount = document.getElementById('ivh-fuzzy-mount');
    if (!mount) return;
    var term = (mount.dataset.term || '').trim() ||
      (new URLSearchParams(location.search).get('q') || '').trim();
    if (!term) return;
    var count = parseInt(mount.dataset.count || '0', 10) || 0;
    if (count > 0) return;      // solo entra cuando el buscador nativo no encontró nada
    var need = 12;

    var existing = {};
    document.querySelectorAll('#product-grid a[href*="/products/"]').forEach(function (a) {
      var m = a.getAttribute('href').match(/\/products\/([^?#]+)/);
      if (m) existing[m[1]] = 1;
    });

    search(term, need + 8).then(function (res) {
      res = res.filter(function (p) { return !existing[p.handle]; }).slice(0, need);
      if (!res.length) return;
      var h = '<div class="ivh-fuzzy-block">';
      var fuzzyStrings = window.ivhStrings || {};
      h += '<p class="ivh-fuzzy-title">' + (fuzzyStrings.searchNoExactMatch || 'No hay una coincidencia exacta para') + ' «' + esc(term) + '». ' + (fuzzyStrings.searchMaybeMeant || 'Quizás buscabas:') + '</p>';
      h += '<ul class="ivh-fuzzy-grid" role="list">';
      h += res.map(function (p) {
        return '<li class="ivh-fuzzy-card"><a href="' + esc(p.url) + '">' +
          '<span class="ivh-fuzzy-img">' + (p.img ? '<img src="' + esc(thumb(p.img, 400)) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="ivh-fuzzy-name">' + esc(p.title) + '</span>' +
          '<span class="ivh-fuzzy-price">' + dinero(p.price) + '</span>' +
        '</a></li>';
      }).join('');
      h += '</ul></div>';
      mount.innerHTML = h;
    });
  }

  function init() { hookDropdown(); hookResultsPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
